import React, { type RefObject } from 'react';
import {
  Box,
  Card,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TextField,
  Chip,
} from '@mui/material';
import { normaliseRating, getRatingColor } from '../utils/reviewRatingUtils';

export interface ObjectiveMeasureRatingCardProps {
  measureName: string;
  level1Criteria: string;
  level2Criteria: string;
  level3Criteria: string;
  value: number | null;
  onRatingChange: (value: number | null) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  error: boolean;
  ratingLabel?: string;
  /** When not set: value === 0 ? 'Reason for N/A *' : 'Comment (optional)' */
  commentLabel?: string;
  /** For manager view: show employee's rating/comment read-only above the form */
  previousRating?: number | string | null;
  previousComment?: string;
  /** When false, N/A option is hidden (e.g. peer objective form in ReviewDetailPage). Default true. */
  allowNa?: boolean;
  onClearError?: () => void;
  inputRef?: RefObject<HTMLElement | null>;
}

const LEVEL_LABELS = {
  1: '1 - Development Required',
  2: '2 - Meets Expectation',
  3: '3 - Exceeds Expectation',
} as const;

export function ObjectiveMeasureRatingCard({
  measureName,
  level1Criteria,
  level2Criteria,
  level3Criteria,
  value,
  onRatingChange,
  comment,
  onCommentChange,
  error,
  ratingLabel = 'Your rating *',
  commentLabel,
  previousRating,
  previousComment,
  allowNa = true,
  onClearError,
  inputRef,
}: ObjectiveMeasureRatingCardProps) {
  const showCriteria =
    level1Criteria !== '—' || level2Criteria !== '—' || level3Criteria !== '—';
  const prevIsNa =
    previousRating === 0 ||
    previousRating === 'NotApplicable' ||
    previousRating === 'Not applicable';
  const prevNormalised = normaliseRating(
    previousRating as number | string | undefined
  );

  const effectiveCommentLabel =
    commentLabel ?? (value === 0 ? 'Reason for N/A *' : 'Comment (optional)');

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {measureName}
      </Typography>

      {showCriteria && (
        <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="600"
            display="block"
            gutterBottom
          >
            Rating criteria
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 0.5 }}
          >
            <strong>Level 1 (Development Required):</strong> {level1Criteria}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 0.5 }}
          >
            <strong>Level 2 (Meets Expectation):</strong> {level2Criteria}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>Level 3 (Exceeds Expectation):</strong> {level3Criteria}
          </Typography>
        </Box>
      )}

      {/* Optional read-only block (manager view: employee's rating) */}
      {previousRating !== undefined && previousRating !== null && (
        <>
          {prevIsNa ? (
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              display="block"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
            >
              <strong>Employee flagged as N/A</strong>
              {previousComment && <>: &quot;{previousComment}&quot;</>}
            </Typography>
          ) : (
            prevNormalised != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                gutterBottom
                display="block"
                component="span"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              >
                <span>Employee rated:</span>
                <Chip
                  label={String(prevNormalised)}
                  size="small"
                  sx={{
                    bgcolor: getRatingColor(prevNormalised),
                    color: 'white',
                    minWidth: 28,
                    height: 28,
                    borderRadius: '50%',
                    '& .MuiChip-label': {
                      px: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                />
                {previousComment && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    &quot;{previousComment}&quot;
                  </Typography>
                )}
              </Typography>
            )
          )}
        </>
      )}

      <FormControl
        fullWidth
        margin="dense"
        size="small"
        error={error}
        ref={inputRef as RefObject<HTMLDivElement>}
      >
        <InputLabel>{ratingLabel}</InputLabel>
        <Select
          value={value ?? ''}
          onChange={(e) => {
            onClearError?.();
            const val =
              e.target.value === '' ? null : Number(e.target.value);
            onRatingChange(val);
          }}
          label={ratingLabel}
        >
          {allowNa && (
            <SelectMenuItem value={0}>
              <em>N/A — exclude from score</em>
            </SelectMenuItem>
          )}
          <SelectMenuItem value={1}>{LEVEL_LABELS[1]}</SelectMenuItem>
          <SelectMenuItem value={2}>{LEVEL_LABELS[2]}</SelectMenuItem>
          <SelectMenuItem value={3}>{LEVEL_LABELS[3]}</SelectMenuItem>
        </Select>
        {error && (
          <FormHelperText>
            {value === 0
              ? 'Please provide a reason in the comment below'
              : 'Please select a rating'}
          </FormHelperText>
        )}
      </FormControl>

      <TextField
        fullWidth
        label={effectiveCommentLabel}
        value={comment}
        onChange={(e) => {
          if (value === 0) onClearError?.();
          onCommentChange(e.target.value);
        }}
        margin="dense"
        size="small"
        multiline
        rows={2}
        error={value === 0 && error}
      />
    </Card>
  );
}
